/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Thu Jun 08 2023
 **/

import { MouseEvent, useRef } from "react";

import { Maybe } from "@/frameworks/libraries/components";
import { Button } from "@/frameworks/designStuff/components";
import { EventID, POPOVER_ID, ZIndex } from "@/frameworks/core/commons";
import { useMinimizeChatList, usePhoneCallMediaState, usePopover } from "@/frameworks/core/hooks";
import { useEventHandler } from "@rfice/eventhandler";
import { useChatRoomListActions, useDMRoomListState } from "@/frameworks/core/chat";

import { MinimizeChatMore, S } from "./private";
import { useScrollMinimizeChatList, MinimizeChatItem } from "./private";

interface MinimizeChatListProps {
	/**
	 * 화면의 왼쪽으로부터 떨어진 정도. 입력값 + 12 만큼 떨어집니다.
	 */
	left?: number;
}

export default function MinimizeChatList({ left = 0 }: MinimizeChatListProps) {
	const wrapperRef = useRef<HTMLDivElement>(null);
	const { useMinimizeChatListRenderer } = useMinimizeChatList();
	const totalChatList = useMinimizeChatListRenderer();
	const { getUIPopupState } = usePhoneCallMediaState();
	const popupState = getUIPopupState();
	const callState = Object.values(popupState)[0];

	const { toggle } = usePopover();

	const { page, lastPage, showAvatarLength, handleClickArrow } = useScrollMinimizeChatList(
		totalChatList,
		wrapperRef.current
	);

	const start = page * showAvatarLength;

	const currentPageChatList = Object.values(totalChatList)
		.sort((a, b) => {
			if (a.new && !b.new) return -1;
			if (!a.new && b.new) return 1;
			return a.createdAt - b.createdAt;
		})
		.slice(start, start + showAvatarLength);

	const handleClickMore = (ev: MouseEvent) => {
		toggle({
			popoverID: POPOVER_ID.MINIMIZE_CHAT_MORE,
			content: <MinimizeChatMore />,
			anchorEl: ev.currentTarget,
			direction: {
				x: "right",
				y: "center"
			},
			position: {
				x: 20,
				y: 0
			},
			zIndex: ZIndex.MINIMIZE_CHAT_LIST
		});
	};

	return (
		<S.Wrapper
			zIndex={callState ? "CHAT_ALARM" : "MINIMIZE_CHAT_LIST"}
			ref={wrapperRef}
			gap={12}
			horizonAlign="center"
			verticalAlign="bottom"
			left={left}
		>
			<Maybe test={page < lastPage} fallback={<S.EmptyBox />}>
				<Button.Icon
					id="up"
					name="Up16"
					size="small"
					shape="circle"
					filled="neutral_secondary"
					css={{ zIndex: 1 }}
					onClick={handleClickArrow}
				/>
			</Maybe>
			<S.ChattingList gap={12} reverse={true} horizonAlign="center" verticalAlign="bottom">
				<Maybe test={Object.keys(totalChatList).length > 2}>
					<Button.Icon
						name="More24"
						size="medium"
						shape="circle"
						filled="neutral_secondary"
						onClick={handleClickMore}
					/>
				</Maybe>
				{currentPageChatList.reverse().map((chat) => (
					<MinimizeChatItem key={chat.id} {...chat} />
				))}
			</S.ChattingList>
			<Maybe test={page > 0} fallback={<S.EmptyBox />}>
				<Button.Icon
					id="down"
					name="Down16"
					size="small"
					shape="circle"
					filled="neutral_secondary"
					css={{ zIndex: 1 }}
					onClick={handleClickArrow}
				/>
			</Maybe>
			<MinimizeChatListProvider />
		</S.Wrapper>
	);
}

function MinimizeChatListProvider() {
	const { minimizeChatListActions } = useMinimizeChatList();
	const chatRoomListActions = useChatRoomListActions();
	const dmList = useDMRoomListState();

	const appendMinimizeDMChat = (roomID: string) => {
		const targetDM = chatRoomListActions.getDMRoom(roomID);
		const users = targetDM
			? targetDM.users.map((user) => ({
					name: user.name,
					userID: user.id,
					src: user.profileImagePath
				}))
			: [];

		minimizeChatListActions.append({ type: "DM", id: roomID, users, new: true });
	};

	const appendMinimizeSpaceChat = ({
		roomID,
		spaceID,
		spaceName
	}: {
		roomID: string;
		spaceID: string;
		spaceName: string;
	}) => {
		minimizeChatListActions.append({
			type: "Space",
			id: roomID,
			spaceID,
			spaceName,
			new: true
		});
	};

	useEventHandler(
		EventID.NOTIFICATION_FIRED,
		(payload: ChatServices.Server.MessageSent.Data) => {
			const { roomId, spaceName, spaceId } = payload;
			const chatRoomType = spaceId ? "Space" : "DM";
			if (chatRoomType === "DM") {
				appendMinimizeDMChat(roomId);
			}
			if (chatRoomType === "Space") {
				appendMinimizeSpaceChat({ roomID: roomId, spaceID: spaceId || "", spaceName: spaceName || "" });
			}
		},
		[dmList]
	);

	return null;
}
